// ---------------------------------------------------------------------------
// InputInfoPane — aba "Instruções" do InputDrawerV3.
// ---------------------------------------------------------------------------
// Concentra propriedades operacionais e instruções de preenchimento.
// A configuração de reuso vive na aba irmã ``AboutPane`` ("Regras de
// reuso").
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { useTranslation } from "react-i18next";

import Create from "@material-ui/icons/Create";
import Tooltip from "@material-ui/core/Tooltip";
import { ThreeDots } from "react-loader-spinner";

import { toast } from "react-toastify";

import useDrawerStyles from "../styles";
import useThemeColors from "../../../../../hooks/useThemeColors";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "../../styles";
import MarkdownEditor from "../MarkdownEditor";
import PaneErrorBanner from "./PaneErrorBanner";
import { PaneLoadingBody } from "../../DrawerPaneLoadingLayout";
import {
  saveInputInstructionsV3,
  suggestInputInstructionsV3,
} from "../../../../../services/inputsV3Service";

const MARKDOWN_REMARK_PLUGINS = [remarkGfm, remarkBreaks];

const notify = (text, type = "info", autoClose = 3000) =>
  toast(text, {
    position: "top-right",
    autoClose,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    type,
  });

const EDITOR_ROLES = new Set(["DEV", "IMG"]);

const InputInfoPane = ({
  input,
  userRole,
  onInstructionsSaved,
  onInstructionsSuggestingChange,
  drawerDetails,
}) => {
  const { t } = useTranslation();
  const DEFAULT_INSTRUCTIONS_PLACEHOLDER = t(
    "modeling_inputs_v3.about_pane.instructions_placeholder_default",
  );
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();
  const canEdit = EDITOR_ROLES.has(userRole);
  const persisted = useMemo(
    () =>
      typeof input?.instructions_md === "string" ? input.instructions_md : "",
    [input?.instructions_md],
  );

  const [draft, setDraft] = useState(persisted);
  // Baseline do "salvo": ``persisted`` reflete o que veio do backend, mas o
  // parent não re-injeta ``instructions_md`` logo após salvar. Mantemos um
  // baseline local atualizado no save para que ``isDirty`` zere na hora.
  const [savedMd, setSavedMd] = useState(persisted);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  // ``null`` (nao ``0``) de proposito: o ``MarkdownEditor`` força a aba
  // "Editar" quando ``forceWriteKey != null``. Iniciar em ``0`` fazia o
  // efeito rodar na montagem (``0 != null``) e sobrescrever a selecao
  // padrao "Visualizar". So vira numero (1, 2, …) ao acionar "Sugerir".
  const [suggestWriteKey, setSuggestWriteKey] = useState(null);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setSavedMd(persisted);
    setDraft((prev) => (prev === persisted || prev === "" ? persisted : prev));
  }, [persisted, input?.meta_name]);

  useEffect(() => {
    if (typeof onInstructionsSuggestingChange === "function") {
      onInstructionsSuggestingChange(suggesting);
    }
    return () => {
      if (typeof onInstructionsSuggestingChange === "function") {
        onInstructionsSuggestingChange(false);
      }
    };
  }, [suggesting, onInstructionsSuggestingChange]);

  const isDirty = canEdit && draft !== savedMd;

  const handleSave = () => {
    if (!canEdit || saving) return;
    const metaName = input?.meta_name;
    if (!metaName) return;
    setSaving(true);
    setSaveError(null);
    saveInputInstructionsV3({
      metaName,
      instructionsMd: draft || null,
    })
      .then((data) => {
        const savedValue =
          typeof data?.instructions_md === "string"
            ? data.instructions_md
            : draft;
        setSavedMd(savedValue);
        if (typeof onInstructionsSaved === "function") {
          onInstructionsSaved(data);
        }
        notify(
          t("modeling_inputs_v3.about_pane.save_success"),
          "success",
          2000,
        );
      })
      .catch((err) => {
        const code = err?.response?.data?.error_code;
        const friendly =
          code === "forbidden_save_instructions"
            ? t("modeling_inputs_v3.about_pane.save_error_forbidden")
            : code === "instructions_too_long"
              ? t("modeling_inputs_v3.about_pane.save_error_too_long")
              : t("modeling_inputs_v3.about_pane.save_error_generic");
        setSaveError(friendly);
        notify(friendly, "error", 3500);
      })
      .finally(() => setSaving(false));
  };

  const handleCancel = () => {
    setDraft(savedMd);
    setSaveError(null);
  };

  const handleSuggest = () => {
    if (!canEdit || suggesting) return;
    const metaName = input?.meta_name;
    if (!metaName) return;
    setSuggesting(true);
    setSaveError(null);
    suggestInputInstructionsV3({ metaName })
      .then((data) => {
        const md =
          typeof data?.instructions_md === "string" ? data.instructions_md : "";
        setDraft(md);
        setSuggestWriteKey((k) => k + 1);
        notify(
          t("modeling_inputs_v3.input_info_pane.suggest_success"),
          "success",
          2500,
        );
      })
      .catch((err) => {
        const code = err?.response?.data?.error_code;
        const friendly =
          code === "forbidden_suggest_instructions"
            ? t("modeling_inputs_v3.input_info_pane.suggest_error_forbidden")
            : code === "llm_unavailable"
              ? t("modeling_inputs_v3.input_info_pane.suggest_error_llm")
              : code === "input_inactive" || code === "input_archived"
                ? t(
                    "modeling_inputs_v3.input_info_pane.suggest_error_ineligible",
                  )
                : t("modeling_inputs_v3.input_info_pane.suggest_error_generic");
        notify(friendly, "error", 4000);
      })
      .finally(() => setSuggesting(false));
  };

  const suggestTooltip = draft.trim()
    ? t("modeling_inputs_v3.input_info_pane.suggest_tooltip_overwrite")
    : t("modeling_inputs_v3.input_info_pane.suggest_tooltip_empty");

  const topics = [
    {
      name: t("modeling_inputs_v3.about_pane.topic_nature"),
      content:
        input?.source_label ||
        t("modeling_inputs_v3.about_pane.topic_nature_default"),
    },
    {
      name: t("modeling_inputs_v3.about_pane.topic_format"),
      content: (() => {
        const types =
          (Array.isArray(input?.expected_file_types) &&
          input.expected_file_types.length
            ? input.expected_file_types
            : input?.expected_file_type
              ? [input.expected_file_type]
              : []) || [];
        if (types.length === 0) return <code>—</code>;
        return (
          <code>{types.map((ft) => String(ft).toUpperCase()).join(" / ")}</code>
        );
      })(),
    },
  ];

  const showFullPaneLoading =
    !drawerDetails?.error &&
    (Boolean(drawerDetails?.loading) || !drawerDetails?.runtimeInput);

  if (showFullPaneLoading) {
    return (
      <div
        ref={wheelRef}
        className={`${classes.paneScroll} ${classes.paneScrollFlexColumn} ${overlayScrollClasses.overlayScroll}`}
      >
        <PaneLoadingBody
          classes={classes}
          clientColor={clientColor}
          message={t("modeling_inputs_v3.input_info_pane.loading_message")}
          slotClass={classes.paneLoadingLayoutSlot}
        />
      </div>
    );
  }

  return (
    <div
      ref={wheelRef}
      className={`${classes.paneScroll} ${overlayScrollClasses.overlayScroll}`}
    >
      {drawerDetails?.error ? (
        <PaneErrorBanner
          title={t("modeling_inputs_v3.input_info_pane.error_title")}
          message={t("modeling_inputs_v3.input_info_pane.error_message")}
          detail={
            drawerDetails.error?.response?.data?.error_code ||
            drawerDetails.error?.message ||
            null
          }
          onRetry={
            typeof drawerDetails.refresh === "function"
              ? drawerDetails.refresh
              : null
          }
          retrying={Boolean(drawerDetails.loading)}
        />
      ) : null}
      <div className={classes.section}>
        <div className={classes.sectionTitle}>
          {t("modeling_inputs_v3.about_pane.section_properties")}
        </div>
        {topics.map((topic) => (
          <div key={topic.name} className={classes.topic}>
            <div className={classes.topicName}>{topic.name}</div>
            <div className={classes.topicContent}>{topic.content}</div>
          </div>
        ))}
      </div>

      <div className={classes.section}>
        <div
          className={classes.sectionTitle}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>{t("modeling_inputs_v3.about_pane.section_instructions")}</span>
          {canEdit && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexShrink: 0,
              }}
            >
              {suggesting ? (
                <span
                  className={classes.toolbarHint}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("modeling_inputs_v3.input_info_pane.suggesting")}
                  <ThreeDots
                    color={clientColor}
                    height="10px"
                    width="32px"
                    wrapperStyle={{ height: "10px", width: "32px" }}
                  />
                </span>
              ) : (
                isDirty && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                      color: "#d32f2f",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {t("modeling_inputs_v3.input_info_pane.unsaved_changes")}
                  </span>
                )
              )}
              <Tooltip title={suggestTooltip} placement="top" arrow>
                <span>
                  <button
                    type="button"
                    className={`${classes.btn} ${classes.btnGhost} ${classes.btnSmall}`}
                    onClick={handleSuggest}
                    disabled={suggesting || saving}
                  >
                    <Create style={{ fontSize: 16 }} />
                    {t("modeling_inputs_v3.input_info_pane.suggest_btn")}
                  </button>
                </span>
              </Tooltip>
            </div>
          )}
        </div>

        {saveError && (
          <PaneErrorBanner
            title={t("modeling_inputs_v3.about_pane.save_error_banner_title")}
            message={t(
              "modeling_inputs_v3.about_pane.save_error_banner_message",
            )}
            detail={saveError}
            onRetry={handleSave}
            retrying={saving}
          />
        )}

        {canEdit ? (
          <>
            <MarkdownEditor
              value={draft}
              onChange={setDraft}
              onSave={isDirty && !suggesting ? handleSave : undefined}
              onCancel={isDirty && !suggesting ? handleCancel : undefined}
              initialMode="preview"
              placeholder={DEFAULT_INSTRUCTIONS_PLACEHOLDER}
              forceWriteKey={suggestWriteKey}
              disabled={suggesting}
            />
            {saving && (
              <div className={classes.muted} style={{ marginTop: 8 }}>
                {t("modeling_inputs_v3.about_pane.saving")}
              </div>
            )}
          </>
        ) : persisted ? (
          <div className={classes.mdPreviewPane}>
            <ReactMarkdown remarkPlugins={MARKDOWN_REMARK_PLUGINS}>
              {persisted}
            </ReactMarkdown>
          </div>
        ) : (
          <div className={classes.muted}>
            {t("modeling_inputs_v3.about_pane.no_instructions")}
          </div>
        )}
      </div>
    </div>
  );
};

InputInfoPane.propTypes = {
  input: PropTypes.object,
  userRole: PropTypes.string,
  onInstructionsSaved: PropTypes.func,
  onInstructionsSuggestingChange: PropTypes.func,
  drawerDetails: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.any,
    runtimeInput: PropTypes.shape({ meta_name: PropTypes.string }),
    refresh: PropTypes.func,
  }),
};

export default InputInfoPane;
